import React, { ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { GroupBase, OptionsOrGroups } from 'react-select';
import api from '../../../../components/api';
import { yupErrorMsg } from '../../../../components/atoms/const';

interface MyFormValueType {
  upc: string;
  name: string;
  unit: number;
  isBatch: boolean;
  description: string;
  category: string | number;
  speciality: number[];
  productId: {value: number, label: string} | null;
  samsClubItemNum: string;
  vistarItemNum: string;
}

function useProductsAddProduct() {
  // Navigation handler
  const navigation = useNavigate();
  const navigateToLanding = () => navigation('/products');

  // Function for separated between category and speciality
  const categoryFilter = (categories: {groupId: number, category: string, id: number}[]) => {
    const speciality: {value: number, label: string}[] = [];
    const category: {value: number, label: string}[] = [];
    for (let i = 0; i < categories.length; i += 1) {
      const data = { value: categories[i].id, label: categories[i].category };
      if (categories[i].groupId === 1) category.push(data);
      else speciality.push(data);
    }
    return {
      speciality, category,
    };
  };

  // Product Category and Speciality List
  const { data: categoryRes, isLoading: categoryLoad } = useQuery(
    ['getAllCategories', 'product'],
    () => api.product.getAllCategories(),
  );
  const categoryList = React.useMemo(() => {
    const resData = categoryRes?.data?.data;
    if (resData) return categoryFilter(resData);
    return {
      category: [],
      speciality: [],
    };
  }, [categoryRes]);

  // const productCategory = (resData: any) => {
  //   const speciality = [];
  //   let category = null;
  //   if (resData) {
  //     console.log('Category Num', resData.length);
  //     for (let i = 0; i < resData.length; i += 1) {
  //       const data = { value: resData[i].id, label: resData[i].category };
  //       if (resData[i].groupId === 1) category = data.value;
  //       else speciality.push(data.value);
  //     }
  //   }
  //   return {
  //     speciality, category,
  //   };
  // };

  // Individual Product Id Search handler
  const [productSearchInput, setProductSearchInput] = React.useState<string>('');
  // eslint-disable-next-line no-unused-vars
  const optionsFetcher = (text : string, callback: (options: OptionsOrGroups<{value: any, label: string}, GroupBase<{value: string, label: string}>>) => void) => {
    api.product.findAll(text)
      .then((res) => {
        const resData = res?.data?.data;
        if (resData) {
          const options = resData.map((product) => ({
            value: product.id,
            label: product.name,
          }));
          callback(options);
        }
      });
  };

  // get productId from url for edit mode
  const { productId } = useParams();
  /*
  *
  *
  *
  * Add or Edit Product Form Handler
  * */
  // API for update or Edit
  // Add New Product
  const { mutate: addProduct, isLoading: addLoad } = useMutation(api.product.add, {
    onMutate: (variables) => console.log(variables),
    onSuccess: (data) => {
      console.log(data);
      alert('success');
      navigateToLanding();
    },
    onError: (error) => {
      console.log(error);
      alert('error');
    },
  });
  // Update Product
  const { mutate: updateProduct, isLoading: updateLoad } = useMutation(api.product.update, {
    onMutate: (variables) => console.log(variables),
    onSuccess: (data) => {
      console.log(data);
      alert('success');
      navigateToLanding();
    },
    onError: (error) => {
      console.log(error);
      alert('error');
    },
  });
  // Form handler
  const yupSchema = yup.object({
    upc: yup.string().required(yupErrorMsg.UPC_EMPTY),
    name: yup.string().required(yupErrorMsg.NAME_EMPTY),
    unit: yup.number().when('isBatch', {
      is: true,
      then: yup.number().min(2, yupErrorMsg.UNIT_BATCH).required(yupErrorMsg.UNIT_EMPTY),
      otherwise: yup.number().min(0, yupErrorMsg.UNIT_POSITIVE).required(yupErrorMsg.UNIT_EMPTY),
    }),
    description: yup.string().required(yupErrorMsg.DESCRIPTION_EMPTY),
    category: yup.string().required(yupErrorMsg.CATEGORIES_EMPTY),
    productId: yup.object().nullable(true).when('isBatch', {
      is: true,
      then: yup.object().nullable(true).required(yupErrorMsg.PRODUCT_EMPTY),
    }),
  });
  const initValues: MyFormValueType = {
    upc: '',
    name: '',
    unit: 0,
    isBatch: false,
    description: '',
    category: '',
    speciality: [],
    productId: null,
    samsClubItemNum: '',
    vistarItemNum: '',
  };
  const formik = useFormik({
    initialValues: initValues,
    validationSchema: yupSchema,
    onSubmit: ((values) => {
      const dataProduct = {
        upc: values.upc,
        name: values.name,
        unit: values.unit,
        isBatch: values.isBatch,
        description: values.description,
        categories: [Number(values.category), ...values.speciality],
      };
      const isBatchData = {
        productId: values.productId?.value,
        samsClubItemNum: values.samsClubItemNum,
        vistarItemNum: values.vistarItemNum,
      };
      if (productId) {
        if (values.isBatch) updateProduct({ ...dataProduct, ...isBatchData, id: Number(productId) });
        else updateProduct({ ...dataProduct, id: Number(productId) });
      } else if (values.isBatch) addProduct({ ...dataProduct, ...isBatchData });
      else addProduct(dataProduct);
    }),
  });

  /*
  *
  *
  *
  * Form check speciality handler
  * */
  const handleChangeSpeciality = (e: ChangeEvent<HTMLInputElement>, value: number) => {
    if (e.target.checked) {
      formik.setFieldValue('speciality', [...formik.values.speciality, value]);
    } else {
      formik.setFieldValue('speciality', formik.values.speciality.filter((x) => x !== value));
    }
  };

  // get product by productId params
  const { data: productByIdRes, isLoading: byIdLoad } = useQuery(
    ['findAll', 'product', ''],
    () => api.product.getById(Number(productId!)),
    { enabled: !!productId },
  );
  const { data: individualProductRes, isLoading: individualLoad } = useQuery(
    ['findAll', 'product', productByIdRes?.data?.data?.productId],
    () => api.product.getById(productByIdRes!.data!.data!.productId),
    { enabled: !!productByIdRes?.data?.data && productByIdRes?.data?.data?.isBatch },
  );

  React.useEffect(() => {
    const resData = productByIdRes?.data?.data;
    if (resData && categoryList.speciality.length && categoryList.category.length && productId) {
      formik.setFieldValue('upc', resData.upc ?? '');
      formik.setFieldValue('name', resData.name ?? '');
      formik.setFieldValue('unit', resData.unit ?? 0);
      formik.setFieldValue('isBatch', resData.isBatch ?? false);
      formik.setFieldValue('description', resData.description ?? '');

      const categories = categoryFilter(resData.categories);
      if (categories.category.length) {
        formik.setFieldValue('category', categories.category[0] ?? null);
      }
      if (categories.speciality.length) {
        formik.setFieldValue('speciality', categories.speciality.map((x) => x.value) ?? []);
      }
      // const productCat = productCategory(resData.categories);
      // const categoryData = categoryList.category.filter((x) => resData.categories?.includes(x.value));
      // formik.setFieldValue('category', categoryData[0] ?? '');
      // formik.setFieldValue('category', productCat.category ?? '');
      // const specialityData = categoryList.speciality.filter((x) => resData.categories?.includes(x.value));
      // formik.setFieldValue('speciality', specialityData ?? []);
      // formik.setFieldValue('speciality', productCat.speciality ?? []);
      const individualData = individualProductRes?.data?.data;
      if (resData.isBatch) {
        if (individualData) {
          formik.setFieldValue('productId', { value: individualData.id, label: individualData.name });
        }
        formik.setFieldValue('samsClubItemNum', resData.samsClubItemNum ?? '');
        formik.setFieldValue('vistarItemNum', resData.vistarItemNum ?? '');
      }
    }
  }, [productByIdRes, categoryList, individualProductRes, productId]);

  return {
    product: {
      params: productId,
      load: byIdLoad,
    },
    category: {
      load: categoryLoad,
      data: categoryList,
    },
    submitLoad: addLoad || updateLoad,
    formik,
    handleChangeSpeciality,
    productId: {
      currentSearch: productSearchInput,
      setCurrentSearch: setProductSearchInput,
      fetcher: optionsFetcher,
    },
    individualLoad,
  };
}

export default useProductsAddProduct;
